/**
 * Authentication Events
 *
 * This module provides a simple event system for handling authentication-related
 * events across the application. It allows the API service layer to communicate
 * with the React auth context without direct coupling.
 */

type AuthEventType = 'unauthorized' | 'session-established';

type AuthEventCallback = () => void;

const listeners = new Map<AuthEventType, Set<AuthEventCallback>>();

/**
 * Subscribe to an auth event
 * @returns Cleanup function to unsubscribe
 */
export function onAuthEvent(
  event: AuthEventType,
  callback: AuthEventCallback
): () => void {
  if (!listeners.has(event)) {
    listeners.set(event, new Set());
  }
  listeners.get(event)?.add(callback);

  return () => {
    listeners.get(event)?.delete(callback);
  };
}

/**
 * Emit an auth event to all subscribers
 */
export function emitAuthEvent(event: AuthEventType): void {
  const callbacks = listeners.get(event);
  if (callbacks) {
    for (const callback of callbacks) {
      callback();
    }
  }
}
